import { useState, useEffect, useRef, useCallback } from 'react';
import { useWordStore, PIPELINE_STAGES } from '../stores/wordStore';
import { useUiStore } from '../stores/uiStore';
import { STAGE_CONFIG } from '../utils/stageConfig';
import './CommandPalette.css';

export default function CommandPalette() {
  const [open, setOpen] = useState<boolean>(false);
  const [query, setQuery] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { words, selectWord } = useWordStore();

  useEffect(() => {
    function onOpen() {
      setOpen(true);
      setQuery('');
    }
    window.addEventListener('pipeline:command-palette', onOpen);
    return () => window.removeEventListener('pipeline:command-palette', onOpen);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const close = useCallback(() => setOpen(false), []);

  const results = query.length > 0
    ? words
        .filter((w) =>
          w.word?.toLowerCase().includes(query.toLowerCase()) ||
          w.correct_definition?.toLowerCase().includes(query.toLowerCase())
        )
        .slice(0, 12)
    : [];

  const handleSelect = useCallback((wordId: string) => {
    useUiStore.getState().setNewWordMode(false);
    selectWord(wordId);
    setOpen(false);
  }, [selectWord]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  }, [close]);

  if (!open) return null;

  return (
    <div className="command-palette-overlay" onClick={close}>
      <div className="command-palette" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          type="text"
          className="command-palette-input"
          placeholder="Search words..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        {results.length > 0 && (
          <div className="command-palette-results">
            {results.map((w) => {
              const stageInfo = PIPELINE_STAGES.find((s) => s.id === w._stage);
              return (
                <button
                  key={w.id}
                  className="command-palette-item"
                  onClick={() => handleSelect(w.id)}
                >
                  <span className="cp-word">{w.word}</span>
                  <span className="cp-stage" style={{ color: stageInfo?.color }}>
                    {STAGE_CONFIG[w._stage]?.label || w._stage}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {query.length > 0 && results.length === 0 && (
          <div className="command-palette-empty">No matches</div>
        )}
      </div>
    </div>
  );
}
